import { useState, useEffect } from "react";
import ImagePicker from "./ImagePicker";
import "./ScheduledEventPicker.css";

/**
 * ScheduledEventPicker - Modal pour créer/modifier un événement programmé
 * 
 * @param {boolean} isOpen - Contrôle l'affichage du modal
 * @param {function} onClose - Callback pour fermer le modal
 * @param {object} event - Événement à modifier (null pour création)
 * @param {string} scheduleCellId - ID de la cellule schedule (requis pour création)
 * @param {function} onSave - Callback avec les données de l'événement
 */
export default function ScheduledEventPicker({ 
    isOpen, 
    onClose, 
    event = null, 
    scheduleCellId,
    onSave 
}) {
    const [formData, setFormData] = useState({
        title: "",
        description: "",
        location: "",
        start_time: "",
        end_time: "",
        logo_uri: ""
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isEditMode = !!event;

    useEffect(() => {
        if (event) {
            // Mode édition : pré-remplir le formulaire
            setFormData({
                title: event.title || "",
                description: event.description || "",
                location: event.location || "",
                start_time: formatDateTimeLocal(event.start_time),
                end_time: formatDateTimeLocal(event.end_time),
                logo_uri: event.logo_uri || ""
            });
        } else {
            // Mode création : réinitialiser
            resetForm();
        }
        setErrors({});
    }, [event, isOpen]);

    const formatDateTimeLocal = (date) => {
        if (!date) return "";
        const d = new Date(date);
        if (isNaN(d.getTime())) return "";
        // Format: YYYY-MM-DDTHH:MM
        const pad = (n) => n.toString().padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    const resetForm = () => {
        setFormData({
            title: "",
            description: "",
            location: "",
            start_time: "",
            end_time: "",
            logo_uri: ""
        });
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error when field is modified
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const validate = () => {
        const newErrors = {};
        
        if (!formData.title.trim()) {
            newErrors.title = "Le titre est requis";
        }
        
        if (!formData.start_time) {
            newErrors.start_time = "La date de début est requise";
        }
        
        if (!formData.end_time) {
            newErrors.end_time = "La date de fin est requise";
        }
        
        if (formData.start_time && formData.end_time) {
            const start = new Date(formData.start_time);
            const end = new Date(formData.end_time);
            if (end <= start) {
                newErrors.end_time = "La date de fin doit être après la date de début";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setIsSubmitting(true);
        try {
            const eventData = {
                ...formData,
                start_time: new Date(formData.start_time).toISOString(),
                end_time: new Date(formData.end_time).toISOString(),
                schedule_cell_id: event?.schedule_cell_id || scheduleCellId
            };

            if (event?.id) {
                eventData.id = event.id;
            }

            await onSave(eventData, isEditMode);
            onClose();
        } catch (error) {
            console.error("Erreur lors de la sauvegarde:", error);
            setErrors({ submit: "Une erreur est survenue lors de la sauvegarde" });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="scheduled-event-overlay" onClick={handleOverlayClick} />
            <div className="scheduled-event-modal">
                <div className="scheduled-event-header">
                    <h3 className="scheduled-event-title">
                        <span className="title-icon">📅</span>
                        {isEditMode ? "Modifier l'événement" : "Nouvel événement"}
                    </h3>
                    <button
                        type="button"
                        className="scheduled-event-close"
                        onClick={onClose}
                    >
                        ✕
                    </button>
                </div>

                <div className="scheduled-event-content">
                    {/* Logo */}
                    <div className="scheduled-event-section scheduled-event-logo-section">
                        <label className="scheduled-event-label">Logo / Image</label>
                        <div className="scheduled-event-logo-picker">
                            <ImagePicker
                                value={formData.logo_uri}
                                onChange={(value) => handleChange("logo_uri", value)}
                            />
                        </div>
                    </div>

                    {/* Titre */}
                    <div className="scheduled-event-section">
                        <label className="scheduled-event-label">
                            Titre <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            className={`scheduled-event-input ${errors.title ? 'error' : ''}`}
                            value={formData.title}
                            onChange={(e) => handleChange("title", e.target.value)}
                            placeholder="Titre de l'événement"
                        />
                        {errors.title && (
                            <span className="scheduled-event-error">{errors.title}</span>
                        )}
                    </div>

                    {/* Description */}
                    <div className="scheduled-event-section">
                        <label className="scheduled-event-label">Description</label>
                        <textarea
                            className="scheduled-event-textarea"
                            value={formData.description}
                            onChange={(e) => handleChange("description", e.target.value)}
                            placeholder="Description de l'événement..."
                            rows={3}
                        />
                    </div>

                    {/* Lieu */}
                    <div className="scheduled-event-section">
                        <label className="scheduled-event-label">
                            <span className="label-icon">📍</span>
                            Lieu
                        </label>
                        <input
                            type="text"
                            className="scheduled-event-input"
                            value={formData.location}
                            onChange={(e) => handleChange("location", e.target.value)}
                            placeholder="Lieu de l'événement"
                        />
                    </div>

                    {/* Dates */}
                    <div className="scheduled-event-dates">
                        <div className="scheduled-event-section">
                            <label className="scheduled-event-label">
                                <span className="label-icon">🕐</span>
                                Début <span className="required">*</span>
                            </label>
                            <input
                                type="datetime-local"
                                className={`scheduled-event-input ${errors.start_time ? 'error' : ''}`}
                                value={formData.start_time}
                                onChange={(e) => handleChange("start_time", e.target.value)}
                            />
                            {errors.start_time && (
                                <span className="scheduled-event-error">{errors.start_time}</span>
                            )}
                        </div>

                        <div className="scheduled-event-section">
                            <label className="scheduled-event-label">
                                <span className="label-icon">🕐</span>
                                Fin <span className="required">*</span>
                            </label>
                            <input
                                type="datetime-local"
                                className={`scheduled-event-input ${errors.end_time ? 'error' : ''}`}
                                value={formData.end_time}
                                onChange={(e) => handleChange("end_time", e.target.value)}
                            />
                            {errors.end_time && (
                                <span className="scheduled-event-error">{errors.end_time}</span>
                            )}
                        </div>
                    </div>

                    {/* Erreur globale */}
                    {errors.submit && (
                        <div className="scheduled-event-submit-error">
                            <span>⚠️</span> {errors.submit}
                        </div>
                    )}
                </div>

                <div className="scheduled-event-footer">
                    <button
                        type="button"
                        className="scheduled-event-btn scheduled-event-btn-cancel"
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        Annuler
                    </button>
                    <button
                        type="button"
                        className="scheduled-event-btn scheduled-event-btn-save"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="btn-spinner"></span>
                                Enregistrement...
                            </>
                        ) : (
                            <>
                                {isEditMode ? "💾 Enregistrer" : "✨ Créer"}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </>
    );
}

